// @ts-nocheck
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme';
import Fetch from '@/utils/Fetch';
import ToastContext from '@/utils/toast-context';
import * as Enums from '@/utils/enums';
import { useRouter } from 'next/router';
import Helper from '@/utils/helper';
import QuoteCard from '@/components/customer-zone/quote/quote-card';
import SCInfiniteScroll from '@/components/sc-controls/layout/sc-infinite-scroll';
import PageContext from '@/utils/page-context';
import SCListCardSection from '@/components/sc-controls/layout/sc-list-card-section';
import useMobileView from '@/hooks/useMobileView';
import NoSSR from '@/utils/no-ssr'
import CompanyService from '@/services/company-service';
import LeadMessage from '@/components/customer-zone/general/lead-message';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'

const pageSize = 10;
const approvedStatus = [Enums.getEnumStringValue(Enums.QuoteStatus, Enums.QuoteStatus.Approved)];
const acceptedStatus = [Enums.getEnumStringValue(Enums.QuoteStatus, Enums.QuoteStatus.Accepted),
Enums.getEnumStringValue(Enums.QuoteStatus, Enums.QuoteStatus.Invoiced)];
const declinedStatus = [Enums.getEnumStringValue(Enums.QuoteStatus, Enums.QuoteStatus.Declined)];

function QuoteList() {

  const router = useRouter();
  const { t, c } = router.query as any; // TODO(tanstack-router): verify router.query → useParams/useSearch

  const toast = useContext<any>(ToastContext);
  const pageContext = useContext<any>(PageContext);
  const [mobileView] = useMobileView();

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t } as any),
    { enabled: !!t }
  );
  const tenant: any = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0 ? tenantData.Results[0] : null;

  const { data: initialQuotesData } = useQuery(
    ['czQuoteListInit', t, c],
    async () => {
      const [approvedQuotes, acceptedQuotes, declinedQuotes, currencySymbol] = await Promise.all([
        Fetch.post({ url: `/CustomerZone/GetQuotes`, params: { pageSize, pageIndex: 0, PopulatedList: false, SortExpression: "QuoteDate", SortDirection: "descending", QuoteStatusIDList: approvedStatus }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
        Fetch.post({ url: `/CustomerZone/GetQuotes`, params: { pageSize, pageIndex: 0, PopulatedList: false, SortExpression: "QuoteDate", SortDirection: "descending", QuoteStatusIDList: acceptedStatus }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
        Fetch.post({ url: `/CustomerZone/GetQuotes`, params: { pageSize, pageIndex: 0, PopulatedList: false, SortExpression: "QuoteDate", SortDirection: "descending", QuoteStatusIDList: declinedStatus }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
        CompanyService.getCustomerZoneCurrencySymbol(t, c, tenant.API),
      ]);
      return { approvedQuotes, acceptedQuotes, declinedQuotes, currencySymbol };
    },
    { enabled: !!tenant }
  );

  const [approvedQuoteResults, setApprovedQuoteResults] = useState<any>(null);
  const [approvedQuoteTotalResults, setApprovedQuoteTotalResults] = useState(0);
  const [approvedCurrentPage, setApprovedCurrentPage] = useState(0);

  const [acceptedQuoteResults, setAcceptedQuoteResults] = useState<any>(null);
  const [acceptedQuoteTotalResults, setAcceptedQuoteTotalResults] = useState(0);
  const [acceptedCurrentPage, setAcceptedCurrentPage] = useState(0);

  const [declinedQuoteResults, setDeclinedQuoteResults] = useState<any>(null);
  const [declinedQuoteTotalResults, setDeclinedQuoteTotalResults] = useState(0);
  const [declinedCurrentPage, setDeclinedCurrentPage] = useState(0);

  const [currencySymbol, setCurrencySymbol] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (tenantData && !tenant) {
      router.replace('/');
    }
  }, [tenantData]);

  useEffect(() => {
    if (initialQuotesData && approvedQuoteResults === null) {
      setApprovedQuoteResults(initialQuotesData.approvedQuotes.Results);
      setApprovedQuoteTotalResults(initialQuotesData.approvedQuotes.TotalResults);
      setAcceptedQuoteResults(initialQuotesData.acceptedQuotes.Results);
      setAcceptedQuoteTotalResults(initialQuotesData.acceptedQuotes.TotalResults);
      setDeclinedQuoteResults(initialQuotesData.declinedQuotes.Results);
      setDeclinedQuoteTotalResults(initialQuotesData.declinedQuotes.TotalResults);
      setCurrencySymbol(initialQuotesData.currencySymbol);
      pageContext.updateTenantName(initialQuotesData.approvedQuotes.headerTenantName);
    }
  }, [initialQuotesData]);

  useEffect(() => {
    if (approvedCurrentPage > 0)
      searchQuotes(approvedStatus, approvedCurrentPage, approvedQuoteResults, setApprovedQuoteResults, setApprovedQuoteTotalResults);
  }, [approvedCurrentPage]);

  useEffect(() => {
    if (acceptedCurrentPage > 0)
      searchQuotes(acceptedStatus, acceptedCurrentPage, acceptedQuoteResults, setAcceptedQuoteResults, setAcceptedQuoteTotalResults);
  }, [acceptedCurrentPage]);

  useEffect(() => {
    if (declinedCurrentPage > 0)
      searchQuotes(declinedStatus, declinedCurrentPage, declinedQuoteResults, setDeclinedQuoteResults, setDeclinedQuoteTotalResults);
  }, [declinedCurrentPage]);

  async function searchQuotes(status: any, currentPage: number, quoteResults: any, setQuoteResults: any, setTotalResults: any) {
    setSearching(true);
    const searchRes = await Fetch.post({
      url: '/CustomerZone/GetQuotes',
      params: {
        pageIndex: currentPage,
        PageSize: pageSize,
        SortExpression: "QuoteDate",
        SortDirection: "descending",
        PopulatedList: false,
        QuoteStatusIDList: status
      },
      toastCtx: toast,
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    });

    if (currentPage < 1) {
      setQuoteResults(searchRes.Results);
      setTotalResults(searchRes.TotalResults);
    } else {
      let temp = [...quoteResults];
      temp.push(...searchRes.Results);
      setQuoteResults(temp);
      setTotalResults(searchRes.TotalResults);
    }
    setSearching(false);
  }

  function quoteClick(quote: any) {
    Helper.nextRouter(router.push, `/customerzone/viewquote?t=${t}&c=${c}&i=${quote.ID}`);
  }

  if (!tenant || approvedQuoteResults === null) return null;

  return (
    <NoSSR>
      <div className={mobileView ? "" : "centered"}
           style={{
             paddingInlineEnd: 'var(--mantine-spacing-sm)',
             minHeight: 'calc(100dvh - 20px)'
           }}
      >
        <LeadMessage customerID={c} tenantID={t} page={"customerzone/quotelist"} />
        <h2 className="page-title">Your Quotes</h2>

        <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
          <div className="loader"></div>
        </div>

        {approvedQuoteResults && approvedQuoteResults.length > 0 ? <>
          <SCListCardSection text="Awaiting Response" colour={Enums.getEnumStringValue(Enums.QuoteStatusColor, Enums.QuoteStatus.Approved) as any} />
          {approvedQuoteResults.map((quote: any, key: any) => {
            return <QuoteCard key={key} quote={quote} onClick={(q: any) => quoteClick(q)} currencySymbol={currencySymbol} />
          })}
          {approvedQuoteResults.length < approvedQuoteTotalResults ?
            <div className="load-more" onClick={() => setApprovedCurrentPage(approvedCurrentPage + 1)}>Load more</div>
            : ""}
        </> : ""}

        {acceptedQuoteResults && acceptedQuoteResults.length > 0 ? <>
          <SCListCardSection text="Accepted" colour={Enums.getEnumStringValue(Enums.QuoteStatusColor, Enums.QuoteStatus.Accepted) as any} />
          {acceptedQuoteResults.map((quote: any, key: any) => {
            return <QuoteCard key={key} quote={quote} onClick={(q: any) => quoteClick(q)} currencySymbol={currencySymbol} />
          })}
          {acceptedQuoteResults.length < acceptedQuoteTotalResults ?
            <div className="load-more" onClick={() => setAcceptedCurrentPage(acceptedCurrentPage + 1)}>Load more</div>
            : ""}
        </> : ""}

        {declinedQuoteResults && declinedQuoteResults.length > 0 ? <>
          <SCListCardSection text="Declined" colour={Enums.getEnumStringValue(Enums.QuoteStatusColor, Enums.QuoteStatus.Declined) as any} />
          {declinedQuoteResults.map((quote: any, key: any) => {
            return <QuoteCard key={key} quote={quote} onClick={(q: any) => quoteClick(q)} currencySymbol={currencySymbol} />
          })}
          {declinedQuoteResults.length < declinedQuoteTotalResults ?
            <div className="load-more" onClick={() => setDeclinedCurrentPage(declinedCurrentPage + 1)}>Load more</div>
            : ""}
        </> : ""}

        {(!approvedQuoteResults || approvedQuoteResults.length === 0)
          && (!acceptedQuoteResults || acceptedQuoteResults.length === 0)
          && (!declinedQuoteResults || declinedQuoteResults.length === 0) ? <>
          <h4>There are no quotes</h4>
        </> : ""}

        <style jsx>{`

        .load-more {
            margin: 0.5rem;
            cursor: pointer;
            font-weight: bold;
            text-align: right;
            ${mobileView ? "" : `max-width: ${layout.listCardWidth};`}
        }

        .load-more:hover {
            text-decoration: underline;
        }

        h2.page-title {
          margin: 0;
          font-size: 1.5rem;
        }

        .container {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          padding: 1.5rem 3rem;
          overflow-x: hidden;
        }
        .column {
          width: 100%;
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 9rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .title {
          align-items: center;
          display: flex;
          color: ${colors.darkPrimary};
          width: 100%;
        }
        .title h1 {
          font-size: 24px;
          margin: 6px 0 0 0;
        }
        .title p {
          font-size: 14px;
          margin: 0;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }
        .end {
          align-items: flex-end;
        }
        .padded {
          padding-bottom: 1rem;
        }
        .row.top-gap {
          margin-top: 2.5rem;
        }
        .heading {
          color: ${colors.blueGrey};
          font-size: 14px;
          margin-bottom: 0.5rem;
        }
        .heading-blue {
          color: ${colors.bluePrimary};
        }
        .no-jobs {
          align-items: center;
          box-sizing: border-box;
          display: none;
          flex-direction: column;
          flex-grow: 1;
          height: 100%;
          justify-content: center;
          position: relative;
          text-align: center;
        }
        .no-jobs-visible {
          display: flex;
        }
        .no-jobs h3 {
          color: ${colors.darkPrimary};
          font-size: 18px;
          line-height: 22px;
          margin: 2rem 0 0;
        }
        .no-jobs p {
          color: ${colors.blueGrey};
          margin: 0.5rem 0 0;
        }
        .no-jobs a {
          align-items: center;
          color: ${colors.bluePrimary};
          cursor: pointer;
          display: flex;
          font-weight: bold;
          margin-top: 1rem;
          text-decoration: none;
        }
        .no-jobs a img{
          margin-right: 0.5rem;
        }
        .wave {
          position: absolute;
          bottom: -1.5rem;
          left: -3rem;
          pointer-events: none;
          right: 0;
          width: calc(100% + 6rem);
        }
        .search-container :global(.search) {
          width: 528px;
        }
        .table-container {
          display: block;
          height: 100%;
          overflow: none;
          position: relative;
        }
        .table-container :global(.table) {
          margin-top: 0;
        }
        .table-container-visible {
          display: block;
        }
        .loading-overlay {
          align-items: center;
          /*background-color: rgba(28, 37, 44, 0.6);*/
          background-color: rgba(245, 248, 251, 0.6);
          bottom: 0;
          border-radius: 8px;
          display: none;
          justify-content: center;
          left: -1rem;
          position: absolute;
          right: -1rem;
          top: 0.5rem;
        }
        .loading-overlay-visible {
          display: flex;
        }
        .loading-overlay :global(.loader) {
          border-color: rgba(28, 37, 44, 0.2);
          border-left-color: ${colors.darkPrimary};
          border-width: 0.25rem;
          display: flex;
          height: 1.5rem;
          width: 1.5rem;
        }
        a {
          text-decoration: none;
        }
        .select-actions {
          display: flex;
          margin-left: -1rem;
        }
        .select-actions :global(.white-action) {
          font-size: 0.875rem;
          margin: 0 0 0 1rem; 
          width: auto;
        }
        .select-actions :global(.icon) {
          left: 1rem !important;
        }
        .download {
          margin-right: 1rem;
          width: 3rem;
        }

        .centered {
          position: relative;
          left: 50%;
          transform: translateX(-50%);
          width: ${layout.listCardWidth};
        }
      `}</style>
      </div>
    </NoSSR>
  );
}

export default QuoteList;

export const Route = createFileRoute('/customerzone/quotelist')({ component: QuoteList })
